import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SpocSelection } from "./spocSelection";
/* eslint-disable import/export */
export abstract class DeleteProjectSpocsRPC implements
  LeoRPC<DeleteProjectSpocsRPC.Request, DeleteProjectSpocsRPC.Response, DeleteProjectSpocsRPC.Errors.Errors>
{
  abstract execute(
    request: DeleteProjectSpocsRPC.Request
  ): Promise<LeoRPCResult<DeleteProjectSpocsRPC.Response, DeleteProjectSpocsRPC.Errors.Errors>>;
}

export namespace DeleteProjectSpocsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    spocSelection: SpocSelection.SpocSelectionDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isDeleteProjectSpocsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly spocSelection: SpocSelection.SpocSelection;
  
  
    constructor(
    projectId: LeoUUID, 
    spocSelection: SpocSelection.SpocSelection
    ) {
      this.projectId = projectId;
      this.spocSelection = spocSelection;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const spocSelection = SpocSelection.fromDTO(getObject(dto, "spocSelection", InvalidRequestSpocSelectionError));
      return new Request(
        projectId,
        spocSelection
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          spocSelection: this.spocSelection.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `DeleteProjectSpocsRPC(projectId='${this.projectId}',spocSelection='${this.spocSelection}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `DeleteProjectSpocsRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSpocId = "INVALID_SPOC_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    RespondentsAssignedToSpoc = "RESPONDENTS_ASSIGNED_TO_SPOC",
    UnableToDisableUser = "UNABLE_TO_DISABLE_USER"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `DeleteProjectSpocsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSpocIdDTO {
      code: string;
    }
    
    export class InvalidSpocId extends LeoRPCError<RPCError> {
      readonly isInvalidSpocId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSpocId;
        super(code);
      }
    
    
      toDTO(): InvalidSpocIdDTO {
        const dto: InvalidSpocIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSpocId.prototype.toString = function toString(): string {
      return `DeleteProjectSpocsRPC.INVALID_SPOC_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `DeleteProjectSpocsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface RespondentsAssignedToSpocDTO {
      code: string;
    }
    
    export class RespondentsAssignedToSpoc extends LeoRPCError<RPCError> {
      readonly isRespondentsAssignedToSpoc: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.RespondentsAssignedToSpoc;
        super(code);
      }
    
    
      toDTO(): RespondentsAssignedToSpocDTO {
        const dto: RespondentsAssignedToSpocDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    RespondentsAssignedToSpoc.prototype.toString = function toString(): string {
      return `DeleteProjectSpocsRPC.RESPONDENTS_ASSIGNED_TO_SPOC()`;
    };
    
    
    export interface UnableToDisableUserDTO {
      code: string;
    }
    
    export class UnableToDisableUser extends LeoRPCError<RPCError> {
      readonly isUnableToDisableUser: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnableToDisableUser;
        super(code);
      }
    
    
      toDTO(): UnableToDisableUserDTO {
        const dto: UnableToDisableUserDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnableToDisableUser.prototype.toString = function toString(): string {
      return `DeleteProjectSpocsRPC.UNABLE_TO_DISABLE_USER()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSpocId | ProjectAlreadyArchived | RespondentsAssignedToSpoc | UnableToDisableUser;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSpocIdDTO | ProjectAlreadyArchivedDTO | RespondentsAssignedToSpocDTO | UnableToDisableUserDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSpocId:
          return new InvalidSpocId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.RespondentsAssignedToSpoc:
          return new RespondentsAssignedToSpoc();
        case RPCError.UnableToDisableUser:
          return new UnableToDisableUser();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends DeleteProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSpocSelectionError extends DeleteProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSpocSelectionError";
  }
}


export class InvalidResponseInvalidProjectIdError extends DeleteProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSpocIdError extends DeleteProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSpocIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends DeleteProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseRespondentsAssignedToSpocError extends DeleteProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentsAssignedToSpocError";
  }
}

export class InvalidResponseUnableToDisableUserError extends DeleteProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnableToDisableUserError";
  }
}

